import { prepareRestaurantProductsOnLoad } from '../../utils/restaurantMode';
import { mergeExpiredProductsForDisplay } from '../inventory/expiredView';
import instantCache from '../../services/instantCache';
import invoiceService from '../../services/invoiceService';
import { logAchatsReload } from '../../utils/achatReloadDebug';
import { filterValidCustomers, mergeCustomersById, mergeSalesById } from '../../utils/salesCacheSync';
import {
  mergePurchasesById,
  mergeProductsById,
  mergeSuppliersById,
  mergeLotsById,
  mergeEntitiesById,
  mergeCustomerDebtsById,
  normalizeLotsField
} from '../../utils/businessStateSync';
import { purgeLegacyStandaloneDomainKeys } from '../../utils/localStorageHealth';
import { markBootLotsApplied } from '../../infrastructure/startup/bootLotResyncGuard';
import { resolveWorkerSessionsForBusiness } from '../../utils/workerSessionsResolve';
import { applySupermarketInvoiceSupplierContactMigration } from '../../utils/supermarketInvoiceSupplierContactMigration';
import { seedProductsReadCache } from '../../utils/productsReadSource';
import { seedLotsReadCache } from '../../utils/lotsReadSource';
import { seedPurchasesReadCache } from '../../utils/purchasesReadSource';
import { seedSuppliersReadCache } from '../../utils/suppliersReadSource';
import { seedCustomersReadCache } from '../../utils/customersReadSource';
import { seedStockDataReadCache } from '../../utils/stockDataReadSource';
import { dispatchBusinessLazyLoadComplete } from '../operations/dailyClosingOrchestrator';
import { markBootGate, measureBoot, setBootMeta } from '../../infrastructure/telemetry/bootTelemetry';
import { scheduleAggregateIndexBackfill } from '../../infrastructure/startup/bootStorageDeferrals';
import { isDashboardShellGateEnabled } from '../../infrastructure/startup/bootFeatureFlags';
import {
  ensureBootIdleNavigationCancel,
  registerDurableBootIdleTask,
  scheduleDurableBootTaskNow
} from '../../infrastructure/startup/bootIdleScheduler';
import {
  ENTITY_FIELD_READ_DEADLINE_MS,
  ENTITY_FIELD_READ_CONCURRENCY,
  ENTITY_RESYNC_DEADLINE_MS,
  PHASE1_ENTITY_READ_BUDGET_MS,
  PHASE2_WRITER_WAIT_MS,
  asArray,
  countLotEntries,
  hasBatchCorePosData,
  warnLoad,
  withDeadline
} from './businessLoadSupport';
import { waitForEntityStoreWritersIdle } from '../../infrastructure/storage/entityStoreActivityState';

export {
  ENTITY_FIELD_READ_DEADLINE_MS,
  PHASE1_ENTITY_READ_BUDGET_MS,
  hasBatchCorePosData,
  withDeadline
} from './businessLoadSupport';

/** POS-critical fields loaded before routing unlocks. */
export const LAZY_BOOT_PRIORITY_FIELDS = [
  'purchases',
  'lots',
  'products',
  'customers',
  'suppliers',
  'stockData',
  'stockLocations',
  'businessInfo',
  'userInfo',
  'metadata'
];

/** Heavy / report fields loaded in the background after first paint. */
export const LAZY_BOOT_DEFERRED_FIELDS = [
  'sales',
  'invoices',
  'orders',
  'expenses',
  'invoicePayments',
  'supplierDebts',
  'customerDebts',
  'customerInvoices',
  'supermarketPayments',
  'supermarketInvoices',
  'expiredProducts',
  'notifications',
  'workerSessions',
  'reports',
  'discrepancyManagement',
  'persistentStockData',
  'offlineQueue'
];

export const SHELL_ONLY_FIELDS = [
  'restaurantDishes',
  'stockConsumptions',
  'restaurantTables',
  'openTickets',
  'drafts',
  'recipes',
  'productionOrders'
];

/** Minimum POS browse shell — Phase 2 dashboard gate. */
export const BOOT_SHELL_FIELDS = [
  'products',
  'lots',
  'stockData',
  'businessInfo',
  'metadata',
  'purchases'
];

/** Remaining phase-1 priority fields applied before dataLoaded. */
export const BOOT_SHELL_PHASE1B_FIELDS = [
  'customers',
  'suppliers',
  'stockLocations',
  'userInfo'
];

export function isLazyBusinessBootEnabled() {
  const flag = process.env.REACT_APP_LAZY_BUSINESS_BOOT;
  return flag !== 'false' && flag !== '0';
}

export async function importPriorityEntityReaders() {
  const [
    { readPurchasesFromBestSource },
    { readLotsFromBestSource },
    { readProductsFromBestSource },
    { readCustomersFromBestSource },
    { readSuppliersFromBestSource },
    { readStockDataFromBestSource },
    { readStockLocationsFromBestSource },
    { readBusinessInfoFromBestSource },
    { readUserInfoFromBestSource },
    { readMetadataFromBestSource }
  ] = await Promise.all([
    import('../../utils/purchasesReadSource'),
    import('../../utils/lotsReadSource'),
    import('../../utils/productsReadSource'),
    import('../../utils/customersReadSource'),
    import('../../utils/suppliersReadSource'),
    import('../../utils/stockDataReadSource'),
    import('../../utils/stockLocationsReadSource'),
    import('../../utils/businessInfoReadSource'),
    import('../../utils/userInfoReadSource'),
    import('../../utils/metadataReadSource')
  ]);

  return {
    purchases: readPurchasesFromBestSource,
    lots: readLotsFromBestSource,
    products: readProductsFromBestSource,
    customers: readCustomersFromBestSource,
    suppliers: readSuppliersFromBestSource,
    stockData: readStockDataFromBestSource,
    stockLocations: readStockLocationsFromBestSource,
    businessInfo: readBusinessInfoFromBestSource,
    userInfo: readUserInfoFromBestSource,
    metadata: readMetadataFromBestSource
  };
}

export async function importAllEntityReaders() {
  const [
    { readPurchasesFromBestSource },
    { readSalesFromBestSource },
    { readLotsFromBestSource },
    { readProductsFromBestSource },
    { readCustomersFromBestSource },
    { readSuppliersFromBestSource },
    { readStockDataFromBestSource },
    { readInvoicesFromBestSource },
    { readOrdersFromBestSource },
    { readExpensesFromBestSource },
    { readInvoicePaymentsFromBestSource },
    { readSupplierDebtsFromBestSource },
    { readCustomerDebtsFromBestSource },
    { readCustomerInvoicesFromBestSource },
    { readSupermarketPaymentsFromBestSource },
    { readSupermarketInvoicesFromBestSource },
    { readExpiredProductsFromBestSource },
    { readNotificationsFromBestSource },
    { readWorkerSessionsFromBestSource },
    { readReportsFromBestSource },
    { readDiscrepancyManagementFromBestSource },
    { readStockLocationsFromBestSource },
    { readPersistentStockDataFromBestSource },
    { readOfflineQueueFromBestSource },
    { readBusinessInfoFromBestSource },
    { readUserInfoFromBestSource },
    { readMetadataFromBestSource }
  ] = await Promise.all([
    import('../../utils/purchasesReadSource'),
    import('../../utils/salesReadSource'),
    import('../../utils/lotsReadSource'),
    import('../../utils/productsReadSource'),
    import('../../utils/customersReadSource'),
    import('../../utils/suppliersReadSource'),
    import('../../utils/stockDataReadSource'),
    import('../../utils/invoicesReadSource'),
    import('../../utils/ordersReadSource'),
    import('../../utils/expensesReadSource'),
    import('../../utils/invoicePaymentsReadSource'),
    import('../../utils/supplierDebtsReadSource'),
    import('../../utils/customerDebtsReadSource'),
    import('../../utils/customerInvoicesReadSource'),
    import('../../utils/supermarketPaymentsReadSource'),
    import('../../utils/supermarketInvoicesReadSource'),
    import('../../utils/expiredProductsReadSource'),
    import('../../utils/notificationsReadSource'),
    import('../../utils/workerSessionsReadSource'),
    import('../../utils/reportsReadSource'),
    import('../../utils/discrepancyManagementReadSource'),
    import('../../utils/stockLocationsReadSource'),
    import('../../utils/persistentStockDataReadSource'),
    import('../../utils/offlineQueueReadSource'),
    import('../../utils/businessInfoReadSource'),
    import('../../utils/userInfoReadSource'),
    import('../../utils/metadataReadSource')
  ]);

  return {
    purchases: readPurchasesFromBestSource,
    sales: readSalesFromBestSource,
    lots: readLotsFromBestSource,
    products: readProductsFromBestSource,
    customers: readCustomersFromBestSource,
    suppliers: readSuppliersFromBestSource,
    stockData: readStockDataFromBestSource,
    invoices: readInvoicesFromBestSource,
    orders: readOrdersFromBestSource,
    expenses: readExpensesFromBestSource,
    invoicePayments: readInvoicePaymentsFromBestSource,
    supplierDebts: readSupplierDebtsFromBestSource,
    customerDebts: readCustomerDebtsFromBestSource,
    customerInvoices: readCustomerInvoicesFromBestSource,
    supermarketPayments: readSupermarketPaymentsFromBestSource,
    supermarketInvoices: readSupermarketInvoicesFromBestSource,
    expiredProducts: readExpiredProductsFromBestSource,
    notifications: readNotificationsFromBestSource,
    workerSessions: readWorkerSessionsFromBestSource,
    reports: readReportsFromBestSource,
    discrepancyManagement: readDiscrepancyManagementFromBestSource,
    stockLocations: readStockLocationsFromBestSource,
    persistentStockData: readPersistentStockDataFromBestSource,
    offlineQueue: readOfflineQueueFromBestSource,
    businessInfo: readBusinessInfoFromBestSource,
    userInfo: readUserInfoFromBestSource,
    metadata: readMetadataFromBestSource
  };
}

/**
 * Parallel field reads that never fail the whole batch.
 * Timed-out / rejected fields are omitted and logged; callers merge partial results.
 * @returns {Promise<Record<string, unknown>>}
 */
export async function readEntityFieldsParallel(businessId, fields, readers, options = {}) {
  const deadlineMs = options.deadlineMs ?? ENTITY_FIELD_READ_DEADLINE_MS;
  const list = Array.isArray(fields) ? fields : [];
  const concurrencyRaw = options.concurrency;
  const concurrency =
    Number.isFinite(concurrencyRaw) && concurrencyRaw > 0
      ? Math.max(1, Math.floor(concurrencyRaw))
      : list.length;
  const batchLabel = options.batchLabel || 'entity_read_batch';
  void batchLabel;

  /**
   * Controlled concurrency — preserve per-field isolation like Promise.allSettled.
   * @param {string[]} items
   * @param {number} limit
   * @param {(field: string) => Promise<[string, unknown]>} mapper
   */
  async function mapSettledWithConcurrency(items, limit, mapper) {
    /** @type {PromiseSettledResult<[string, unknown]>[]} */
    const settled = new Array(items.length);
    let nextIndex = 0;

    async function worker() {
      while (nextIndex < items.length) {
        const i = nextIndex;
        nextIndex += 1;
        try {
          settled[i] = { status: 'fulfilled', value: await mapper(items[i]) };
        } catch (reason) {
          settled[i] = { status: 'rejected', reason };
        }
      }
    }

    const workerCount = Math.min(limit, items.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return settled;
  }

  const settled =
    list.length === 0
      ? []
      : await mapSettledWithConcurrency(list, concurrency, async (field) => {
          const reader = readers?.[field];
          if (typeof reader !== 'function') {
            throw new Error(`missing reader for ${field}`);
          }
          const value = await withDeadline(
            Promise.resolve(reader(businessId)),
            deadlineMs,
            `read:${field}`
          );
          return [field, value];
        });

  const data = {};
  const failed = [];
  for (let i = 0; i < settled.length; i++) {
    const field = list[i];
    const result = settled[i];
    if (result.status === 'fulfilled') {
      const [key, value] = result.value;
      data[key] = value;
    } else {
      failed.push({
        field,
        error: result.reason?.message || String(result.reason || 'unknown')
      });
      warnLoad(`field read failed: ${field}`, result.reason);
    }
  }

  if (failed.length > 0) {
    markBootGate('business_field_reads_partial', {
      failedCount: failed.length,
      fields: failed.map((f) => f.field)
    });
    if (Array.isArray(options.failedOut)) {
      options.failedOut.push(...failed);
    }
  }

  return data;
}

function batchFieldHasData(field, batchData) {
  switch (field) {
    case 'products':
      return (batchData.products?.length ?? 0) > 0;
    case 'purchases':
      return (batchData.purchases?.length ?? 0) > 0;
    case 'lots':
      return countLotEntries(batchData.lots) > 0;
    case 'suppliers':
      return (batchData.suppliers?.length ?? 0) > 0;
    case 'customers':
      return (batchData.customers?.length ?? 0) > 0;
    case 'stockData':
      return batchData.stockData && Object.keys(batchData.stockData).length > 0;
    case 'stockLocations':
      return (batchData.stockLocations?.length ?? 0) > 0;
    case 'businessInfo':
      return batchData.businessInfo != null;
    case 'userInfo':
      return batchData.userInfo != null;
    case 'metadata':
      return batchData.metadata && Object.keys(batchData.metadata).length > 0;
    default:
      return false;
  }
}

export function getFieldsNeedingEntityRead(fields, batchData, { entityAuthority, noKvBlobMode, shellBlob }) {
  if (entityAuthority || noKvBlobMode || shellBlob) {
    return fields;
  }
  return fields.filter((field) => !batchFieldHasData(field, batchData));
}

/** Sync seed — avoids re-reading KV/IDB during boot when batch already has data. */
export function seedPriorityReadCachesSync(businessId, batchData) {
  if (!businessId || !batchData) return;
  const bid = String(businessId);
  if (batchData.products !== undefined) seedProductsReadCache(bid, batchData.products);
  if (batchData.lots !== undefined) seedLotsReadCache(bid, batchData.lots);
  if (batchData.purchases !== undefined) seedPurchasesReadCache(bid, batchData.purchases);
  if (batchData.suppliers !== undefined) seedSuppliersReadCache(bid, batchData.suppliers);
  if (batchData.customers !== undefined) seedCustomersReadCache(bid, batchData.customers);
  if (batchData.stockData !== undefined) seedStockDataReadCache(bid, batchData.stockData);
}

export function mergeCanonicalField(batchData, field, value, { businessId }) {
  switch (field) {
    case 'purchases':
      batchData.purchases = value;
      break;
    case 'sales':
      batchData.sales = value;
      break;
    case 'lots':
      batchData.lots = value;
      break;
    case 'products':
      batchData.products = value;
      break;
    case 'customers':
      batchData.customers = (value || []).filter(
        (customer) => customer && customer.customerId && typeof customer === 'object'
      );
      break;
    case 'suppliers':
      batchData.suppliers = value;
      break;
    case 'stockData':
      batchData.stockData = value;
      break;
    case 'invoices':
      batchData.invoices = value;
      break;
    case 'orders':
      batchData.orders = value;
      break;
    case 'expenses':
      batchData.expenses = value;
      break;
    case 'invoicePayments':
      batchData.invoicePayments = value;
      break;
    case 'supplierDebts':
      batchData.supplierDebts = value;
      break;
    case 'customerDebts':
      batchData.customerDebts = value;
      break;
    case 'customerInvoices':
      batchData.customerInvoices = value;
      break;
    case 'supermarketPayments':
      batchData.supermarketPayments = value;
      break;
    case 'supermarketInvoices':
      batchData.supermarketInvoices = invoiceService.consolidateInvoicesInMemory(
        Array.isArray(value) ? value : []
      );
      break;
    case 'expiredProducts':
      batchData.expiredProducts = mergeExpiredProductsForDisplay(
        value,
        batchData.lots,
        batchData.products
      );
      break;
    case 'notifications':
      batchData.notifications = value;
      break;
    case 'workerSessions':
      batchData.workerSessions = resolveWorkerSessionsForBusiness(businessId, value);
      break;
    case 'reports':
      batchData.reports = value;
      break;
    case 'discrepancyManagement': {
      const dm = value || {};
      batchData.discrepancyManagement = {
        selfDeviations: dm.selfDeviations || [],
        handoverDeviations: dm.handoverDeviations || [],
        workerTolerances: dm.workerTolerances || {},
        pendingReviews: dm.pendingReviews || [],
        auditTrail: dm.auditTrail || [],
        notifications: dm.notifications || [],
        toleranceSystemEnabled: dm.toleranceSystemEnabled || false
      };
      break;
    }
    case 'stockLocations':
      batchData.stockLocations = value;
      break;
    case 'persistentStockData':
      batchData.persistentStockData = value;
      break;
    case 'offlineQueue':
      batchData.offlineQueue = value;
      break;
    case 'businessInfo':
      batchData.businessInfo = value;
      break;
    case 'userInfo':
      batchData.userInfo = value;
      break;
    case 'metadata':
      batchData.metadata = value;
      break;
    default:
      break;
  }
}

export function mergeCanonicalFields(batchData, canonicalByField, context) {
  for (const [field, value] of Object.entries(canonicalByField)) {
    mergeCanonicalField(batchData, field, value, context);
  }
}

/**
 * Formerly rehydrated entity_* from legacy business_*_data when core fields
 * were empty under NO_KV_BLOB. That pulled data from the wrong store on every
 * reload — removed: entity_* is sole authority in noKvBlob mode.
 */
export async function resyncEntityStoresIfCoreEmpty(
  _batchData,
  _businessId,
  noKvBlobMode,
  _readers
) {
  if (noKvBlobMode) return;
}

export async function applyRestaurantProductPrep(batchData, businessId) {
  const bizTypeForMigration =
    batchData.businessInfo?.business_type || batchData.metadata?.business_type;
  const restaurantPrep = prepareRestaurantProductsOnLoad(
    bizTypeForMigration,
    batchData.products,
    batchData.restaurantDishes
  );
  batchData.products = restaurantPrep.products;
  if (restaurantPrep.clearRestaurantDishes) {
    batchData.restaurantDishes = [];
  }

  const { ensureRestaurantTablesSeeded } = await import('../restaurant/tableService');
  const tableSeed = ensureRestaurantTablesSeeded(
    bizTypeForMigration,
    batchData.restaurantTables
  );
  batchData.restaurantTables = tableSeed.tables;
  if (!Array.isArray(batchData.openTickets)) {
    batchData.openTickets = [];
  }
  if (!Array.isArray(batchData.drafts)) {
    batchData.drafts = [];
  }

  // Phase 3: seed Recipe v1 from dish includedStockItems when missing.
  const { seedRecipesFromDishIncludedStock } = await import(
    '../restaurant/recipe/legacyRecipeMigration'
  );
  const recipeSeed = seedRecipesFromDishIncludedStock(
    batchData.products,
    batchData.recipes
  );
  batchData.recipes = recipeSeed.recipes;

  const { prepareRestaurantStockLocationsOnLoad } = await import(
    '../restaurant/restaurantStockLocations'
  );
  const locationSeed = prepareRestaurantStockLocationsOnLoad(
    bizTypeForMigration,
    batchData.stockLocations
  );
  batchData.stockLocations = locationSeed.stockLocations;

  if (
    !restaurantPrep.persistProducts &&
    !restaurantPrep.clearRestaurantDishes &&
    !tableSeed.seeded &&
    !recipeSeed.seeded &&
    !locationSeed.seeded
  ) {
    return;
  }

  try {
    const { localStorageManager } = await import('../../services/localStorageManager');
    if (restaurantPrep.persistProducts) {
      await localStorageManager.setBusinessData('products', batchData.products, businessId);
    }
    if (restaurantPrep.clearRestaurantDishes) {
      await localStorageManager.setBusinessData('restaurantDishes', [], businessId);
    }
    if (tableSeed.seeded) {
      await localStorageManager.setBusinessData(
        'restaurantTables',
        batchData.restaurantTables,
        businessId
      );
    }
    if (recipeSeed.seeded) {
      await localStorageManager.setBusinessData('recipes', batchData.recipes, businessId);
    }
    if (locationSeed.seeded) {
      await localStorageManager.setBusinessData(
        'stockLocations',
        batchData.stockLocations,
        businessId
      );
    }
  } catch (persistErr) {
    console.warn('[DataContext] restaurant migration persist failed:', persistErr);
  }
}

/** Backfill legacy invoice supplier contact fields once suppliers are loaded. */
export async function postProcessSupermarketInvoicesBatch(batchData, businessId) {
  if (!businessId || !batchData) {
    return { changedCount: 0, persisted: false };
  }
  const invoices = batchData.supermarketInvoices;
  if (!Array.isArray(invoices) || invoices.length === 0) {
    return { changedCount: 0, persisted: false };
  }

  const result = await applySupermarketInvoiceSupplierContactMigration(
    businessId,
    invoices,
    batchData.suppliers || []
  );
  batchData.supermarketInvoices = result.invoices;
  return result;
}

export function applyBatchFieldsToSetters(
  batchData,
  setters,
  fields,
  {
    startTransition,
    setDataLoaded,
    markDataLoaded = false,
    setDataShellReady,
    markDataShellReady = false,
    urgent = false,
    /** Business switch: drop the previous tenant's rows instead of union-merging ids. */
    replace = false
  }
) {
  const fieldSet = new Set(fields);
  /** In replace mode ignore absent fields — a failed read must not blank the list. */
  const takeList = (next, prev, merge) => {
    if (!replace) return merge();
    return Array.isArray(next) ? next : prev;
  };
  const {
    setProducts,
    setSuppliers,
    setPurchases,
    setOrders,
    setCustomers,
    setSales,
    setExpiredProducts,
    setInvoices,
    setInvoicePayments,
    setSupermarketPayments,
    setSupermarketInvoices,
    setSupplierDebts,
    setCustomerDebts,
    setCustomerInvoices,
    setExpenses,
    setRestaurantDishes,
    setStockConsumptions,
    setRestaurantTables,
    setOpenTickets,
    setRecipes,
    setProductionOrders,
    setLots,
    setStockData,
    setWorkerSessions,
    setNotifications,
    setReports,
    setStockLocations,
    setPersistentStockData,
    setOfflineQueue,
    setBusinessInfo,
    setUserInfo,
    setMetadata,
    setDiscrepancyManagement
  } = setters;

  const run = () => {
    if (fieldSet.has('products')) {
      setProducts((prev) =>
        takeList(batchData.products, prev, () => mergeProductsById(prev, batchData.products))
      );
    }
    if (fieldSet.has('suppliers')) {
      setSuppliers((prev) =>
        takeList(batchData.suppliers, prev, () => mergeSuppliersById(prev, batchData.suppliers))
      );
    }
    if (fieldSet.has('purchases')) {
      setPurchases((prev) =>
        takeList(batchData.purchases, prev, () => mergePurchasesById(prev, batchData.purchases))
      );
    }
    if (fieldSet.has('orders')) {
      setOrders((prev) =>
        takeList(batchData.orders, prev, () =>
          mergeEntitiesById(prev, batchData.orders, ['id', 'orderId'])
        )
      );
    }
    if (fieldSet.has('customers')) {
      setCustomers((prev) =>
        filterValidCustomers(
          takeList(batchData.customers, prev, () => mergeCustomersById(prev, batchData.customers))
        )
      );
    }
    if (fieldSet.has('sales')) {
      setSales((prev) =>
        takeList(batchData.sales, prev, () => mergeSalesById(prev, batchData.sales))
      );
    }
    if (fieldSet.has('expiredProducts')) {
      setExpiredProducts(batchData.expiredProducts);
    }
    if (fieldSet.has('invoices')) {
      setInvoices((prev) =>
        takeList(batchData.invoices, prev, () =>
          mergeEntitiesById(prev, batchData.invoices, ['id', 'invoiceId'])
        )
      );
    }
    if (fieldSet.has('invoicePayments')) {
      setInvoicePayments((prev) =>
        takeList(batchData.invoicePayments, prev, () =>
          mergeEntitiesById(prev, batchData.invoicePayments, ['id'])
        )
      );
    }
    if (fieldSet.has('supermarketPayments')) {
      setSupermarketPayments((prev) =>
        takeList(batchData.supermarketPayments, prev, () =>
          mergeEntitiesById(prev, batchData.supermarketPayments, ['id'])
        )
      );
    }
    if (fieldSet.has('supermarketInvoices')) {
      setSupermarketInvoices((prev) =>
        takeList(batchData.supermarketInvoices, prev, () =>
          // Startup CON cleaner persists; avoid write-on-every-load (reload loops)
          invoiceService.mergeAndConsolidateInvoices(prev, batchData.supermarketInvoices, ['id'])
        )
      );
    }
    if (fieldSet.has('supplierDebts')) {
      setSupplierDebts((prev) =>
        takeList(batchData.supplierDebts, prev, () =>
          mergeEntitiesById(prev, batchData.supplierDebts, ['id'])
        )
      );
    }
    if (fieldSet.has('customerDebts')) {
      setCustomerDebts((prev) =>
        takeList(batchData.customerDebts, prev, () =>
          mergeCustomerDebtsById(prev, batchData.customerDebts)
        )
      );
    }
    if (fieldSet.has('customerInvoices')) {
      setCustomerInvoices((prev) =>
        takeList(batchData.customerInvoices, prev, () =>
          mergeEntitiesById(prev, batchData.customerInvoices, ['id'])
        )
      );
    }
    if (fieldSet.has('expenses')) {
      setExpenses((prev) =>
        takeList(batchData.expenses, prev, () =>
          mergeEntitiesById(prev, batchData.expenses, ['id'])
        )
      );
    }
    if (fieldSet.has('restaurantDishes')) {
      setRestaurantDishes(batchData.restaurantDishes);
    }
    if (fieldSet.has('stockConsumptions')) {
      const loaded = Array.isArray(batchData.stockConsumptions)
        ? batchData.stockConsumptions
        : [];
      setStockConsumptions((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        // Empty shell must not wipe in-memory cuisine withdrawals after a late boot apply.
        if (loaded.length === 0) return prevArr;
        if (prevArr.length === 0) return loaded;
        return mergeEntitiesById(prevArr, loaded, ['id']);
      });
    }
    if (fieldSet.has('recipes') && typeof setRecipes === 'function') {
      const loaded = Array.isArray(batchData.recipes) ? batchData.recipes : [];
      setRecipes((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        // Empty shell must not wipe in-memory recipes (fat blob wins score → empty recipes).
        if (loaded.length === 0) return prevArr;
        if (prevArr.length === 0) return loaded;
        return mergeEntitiesById(prevArr, loaded, ['id']);
      });
    }
    if (fieldSet.has('productionOrders') && typeof setProductionOrders === 'function') {
      const loaded = Array.isArray(batchData.productionOrders)
        ? batchData.productionOrders
        : [];
      setProductionOrders((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        if (loaded.length === 0) return prevArr;
        if (prevArr.length === 0) return loaded;
        return mergeEntitiesById(prevArr, loaded, ['id']);
      });
    }
    if (fieldSet.has('restaurantTables') && typeof setRestaurantTables === 'function') {
      setRestaurantTables(batchData.restaurantTables || []);
    }
    if (fieldSet.has('openTickets') && typeof setOpenTickets === 'function') {
      setOpenTickets(batchData.openTickets || []);
    }
    if (fieldSet.has('lots')) {
      setLots((prev) => {
        const prevLots = Array.isArray(prev) ? prev : normalizeLotsField(prev);
        const loadedLots = Array.isArray(batchData.lots)
          ? batchData.lots
          : normalizeLotsField(batchData.lots);
        if (replace) {
          return batchData.lots == null ? prevLots : loadedLots;
        }
        return mergeLotsById(prevLots, loadedLots);
      });
      markBootLotsApplied();
    }
    if (fieldSet.has('stockData')) {
      setStockData(batchData.stockData);
    }
    if (fieldSet.has('workerSessions')) {
      setWorkerSessions(batchData.workerSessions);
    }
    if (fieldSet.has('notifications')) {
      setNotifications(batchData.notifications);
    }
    if (fieldSet.has('reports')) {
      setReports(batchData.reports);
    }
    if (fieldSet.has('stockLocations')) {
      setStockLocations(batchData.stockLocations);
    }
    if (fieldSet.has('persistentStockData')) {
      setPersistentStockData(batchData.persistentStockData);
    }
    if (fieldSet.has('offlineQueue')) {
      setOfflineQueue(batchData.offlineQueue);
    }
    if (fieldSet.has('businessInfo')) {
      setBusinessInfo(batchData.businessInfo);
    }
    if (fieldSet.has('userInfo')) {
      setUserInfo(batchData.userInfo);
    }
    if (fieldSet.has('metadata')) {
      setMetadata(batchData.metadata);
    }
    if (fieldSet.has('discrepancyManagement')) {
      setDiscrepancyManagement(batchData.discrepancyManagement);
    }
    if (markDataLoaded && setDataLoaded) {
      setDataLoaded(true);
    }
    if (markDataShellReady && setDataShellReady) {
      setDataShellReady(true);
    }
  };

  if (urgent) {
    run();
  } else {
    startTransition(run);
  }
}

function buildInstantCachePayload(batchData) {
  // POS-critical entities only. Do NOT seed sales/orders/invoices into InstantCache —
  // DataContext is SSOT; cloning history doubles heap for the whole session.
  return {
    products: batchData.products,
    purchases: batchData.purchases,
    customers: batchData.customers,
    suppliers: batchData.suppliers,
    lots: batchData.lots,
    settings: [],
    expiredProducts: batchData.expiredProducts,
    discrepancyManagement: batchData.discrepancyManagement,
    stockData: batchData.stockData,
    stockLocations: batchData.stockLocations || ['Principal'],
    drafts: Array.isArray(batchData.drafts) ? batchData.drafts : []
  };
}

export function initializeInstantCacheFromBatch(batchData, businessId = null, { force = false } = {}) {
  try {
    const payload = buildInstantCachePayload(batchData);
    const bootHash = instantCache.calculateDataHash({
      products: payload.products || [],
      lots: payload.lots || [],
      purchases: payload.purchases || [],
      suppliers: payload.suppliers || []
    });
    const shouldForce = force || !instantCache.isInitialized;
    if (!shouldForce && instantCache.isInitialized && instantCache.lastDataHash === bootHash) {
      return;
    }
    instantCache.syncPosBootData(payload, {
      businessId,
      force: shouldForce,
      fastBoot: true
    });
    // Drop any legacy sales/orders clone left from a prior session in the same tab.
    instantCache.cache?.delete?.('sales');
    instantCache.cache?.delete?.('orders');
    instantCache.cache?.delete?.('invoices');
    instantCache.cache?.delete?.('invoicePayments');
    if (Array.isArray(batchData.sales)) {
      instantCache.cache?.set?.('salesCount', batchData.sales.length);
    }
    markBootGate('instant_cache_boot_done', {
      productsCount: payload.products?.length ?? 0
    });
  } catch {
    // instant cache init is best-effort
  }
}

export function updateInstantCacheDeferredFields(batchData) {
  try {
    // Counts + small derived fields only — never clone sales/orders/invoices history.
    // Surfaces pass sales via ensureWarm({ data }) when aggregates are needed.
    if (!instantCache?.cache?.set) return;
    if (batchData.expiredProducts !== undefined) {
      instantCache.cache.set('expiredProducts', batchData.expiredProducts);
    }
    if (batchData.discrepancyManagement !== undefined) {
      instantCache.cache.set('discrepancyManagement', batchData.discrepancyManagement);
    }
    if (Array.isArray(batchData.sales)) {
      instantCache.cache.set('salesCount', batchData.sales.length);
    }
    if (Array.isArray(batchData.drafts)) {
      instantCache.cache.set('drafts', batchData.drafts);
    }
    instantCache.cache.delete('sales');
    instantCache.cache.delete('orders');
    instantCache.cache.delete('invoices');
    instantCache.cache.delete('invoicePayments');
    if (instantCache.cache.has('products')) {
      instantCache.isInitialized = true;
    }
  } catch {
    // partial cache update is best-effort
  }
}

export async function runAggregateIndexBackfill(batchData, businessId) {
  try {
    const { syncAggregateIndexFromPayload, isDomainAggregateIndexEnabled } = await import(
      '../../infrastructure/storage/domainAggregateStore'
    );
    const { refreshSalesReadCache } = await import('../../utils/salesReadSource');
    const { refreshPurchasesReadCache } = await import('../../utils/purchasesReadSource');
    const { refreshLotsReadCache } = await import('../../utils/lotsReadSource');
    const { refreshProductsReadCache } = await import('../../utils/productsReadSource');
    const { refreshCustomersReadCache } = await import('../../utils/customersReadSource');
    const { refreshSuppliersReadCache } = await import('../../utils/suppliersReadSource');
    const { refreshStockDataReadCache } = await import('../../utils/stockDataReadSource');
    const { refreshInvoicesReadCache } = await import('../../utils/invoicesReadSource');
    const { refreshOrdersReadCache } = await import('../../utils/ordersReadSource');
    const { refreshExpensesReadCache } = await import('../../utils/expensesReadSource');
    const { refreshInvoicePaymentsReadCache } = await import('../../utils/invoicePaymentsReadSource');
    const { refreshSupplierDebtsReadCache } = await import('../../utils/supplierDebtsReadSource');
    const { refreshCustomerDebtsReadCache } = await import('../../utils/customerDebtsReadSource');
    const { refreshCustomerInvoicesReadCache } = await import('../../utils/customerInvoicesReadSource');
    const { refreshSupermarketPaymentsReadCache } = await import(
      '../../utils/supermarketPaymentsReadSource'
    );
    const { refreshSupermarketInvoicesReadCache } = await import(
      '../../utils/supermarketInvoicesReadSource'
    );
    const { refreshExpiredProductsReadCache } = await import('../../utils/expiredProductsReadSource');
    const { refreshNotificationsReadCache } = await import('../../utils/notificationsReadSource');
    const { refreshWorkerSessionsReadCache } = await import('../../utils/workerSessionsReadSource');
    const { refreshReportsReadCache } = await import('../../utils/reportsReadSource');
    const { refreshDiscrepancyManagementReadCache } = await import(
      '../../utils/discrepancyManagementReadSource'
    );
    const { refreshStockLocationsReadCache } = await import('../../utils/stockLocationsReadSource');
    const { refreshPersistentStockDataReadCache } = await import(
      '../../utils/persistentStockDataReadSource'
    );
    const { refreshOfflineQueueReadCache } = await import('../../utils/offlineQueueReadSource');
    const { refreshBusinessInfoReadCache } = await import('../../utils/businessInfoReadSource');
    const { refreshUserInfoReadCache } = await import('../../utils/userInfoReadSource');
    const { refreshMetadataReadCache } = await import('../../utils/metadataReadSource');

    if (isDomainAggregateIndexEnabled()) {
      await syncAggregateIndexFromPayload(
        businessId,
        {
          sales: batchData.sales,
          customers: batchData.customers,
          purchases: batchData.purchases,
          lots: batchData.lots,
          products: batchData.products,
          suppliers: batchData.suppliers,
          stockData: batchData.stockData,
          invoices: batchData.invoices,
          orders: batchData.orders,
          expenses: batchData.expenses,
          invoicePayments: batchData.invoicePayments,
          supplierDebts: batchData.supplierDebts,
          customerDebts: batchData.customerDebts,
          customerInvoices: batchData.customerInvoices,
          supermarketPayments: batchData.supermarketPayments,
          supermarketInvoices: batchData.supermarketInvoices,
          expiredProducts: batchData.expiredProducts,
          notifications: batchData.notifications,
          workerSessions: batchData.workerSessions,
          reports: batchData.reports,
          discrepancyManagement: batchData.discrepancyManagement,
          stockLocations: batchData.stockLocations,
          persistentStockData: batchData.persistentStockData,
          offlineQueue: batchData.offlineQueue,
          businessInfo: batchData.businessInfo,
          userInfo: batchData.userInfo,
          metadata: batchData.metadata
        },
        [
          'sales',
          'customers',
          'purchases',
          'lots',
          'products',
          'suppliers',
          'stockData',
          'invoices',
          'orders',
          'expenses',
          'invoicePayments',
          'supplierDebts',
          'customerDebts',
          'customerInvoices',
          'supermarketPayments',
          'supermarketInvoices',
          'expiredProducts',
          'notifications',
          'workerSessions',
          'reports',
          'discrepancyManagement',
          'stockLocations',
          'persistentStockData',
          'offlineQueue',
          'businessInfo',
          'userInfo',
          'metadata'
        ]
      );
    }

    await Promise.all([
      refreshSalesReadCache(businessId, batchData.sales),
      refreshPurchasesReadCache(businessId, batchData.purchases),
      refreshLotsReadCache(businessId, batchData.lots),
      refreshProductsReadCache(businessId, batchData.products),
      refreshCustomersReadCache(businessId, batchData.customers),
      refreshSuppliersReadCache(businessId, batchData.suppliers),
      refreshStockDataReadCache(businessId, batchData.stockData),
      refreshInvoicesReadCache(businessId, batchData.invoices),
      refreshOrdersReadCache(businessId, batchData.orders),
      refreshExpensesReadCache(businessId, batchData.expenses),
      refreshInvoicePaymentsReadCache(businessId, batchData.invoicePayments),
      refreshSupplierDebtsReadCache(businessId, batchData.supplierDebts),
      refreshCustomerDebtsReadCache(businessId, batchData.customerDebts),
      refreshCustomerInvoicesReadCache(businessId, batchData.customerInvoices),
      refreshSupermarketPaymentsReadCache(businessId, batchData.supermarketPayments),
      refreshSupermarketInvoicesReadCache(businessId, batchData.supermarketInvoices),
      refreshExpiredProductsReadCache(businessId, batchData.expiredProducts),
      refreshNotificationsReadCache(businessId, batchData.notifications),
      refreshWorkerSessionsReadCache(businessId, batchData.workerSessions),
      refreshReportsReadCache(businessId, batchData.reports),
      refreshDiscrepancyManagementReadCache(businessId, batchData.discrepancyManagement),
      refreshStockLocationsReadCache(businessId, batchData.stockLocations),
      refreshPersistentStockDataReadCache(businessId, batchData.persistentStockData),
      refreshOfflineQueueReadCache(businessId, batchData.offlineQueue),
      refreshBusinessInfoReadCache(businessId, batchData.businessInfo),
      refreshUserInfoReadCache(businessId, batchData.userInfo),
      refreshMetadataReadCache(businessId, batchData.metadata)
    ]);
  } catch (indexErr) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[DataContext] aggregate index backfill skipped:', indexErr?.message);
    }
  }
}

/** Business whose rows are currently in the setters — a different one means replace. */
let appliedBusinessKey = null;

/**
 * True exactly once per new business: the first Phase-1 apply for that tenant must
 * replace instead of merge, so ids from the previous business cannot survive.
 * Reloads of the same business keep merging (phases are additive).
 */
function consumeBusinessSwitchReplace(businessId) {
  const key = businessId == null ? '' : String(businessId);
  if (appliedBusinessKey === key) return false;
  appliedBusinessKey = key;
  return true;
}

export function resetAppliedBusinessKey() {
  appliedBusinessKey = null;
}

function scheduleDeferredLoad(deferredTask) {
  // Durable: hashchange cancel must not leave Sales/Orders/Expenses empty forever.
  ensureBootIdleNavigationCancel();
  registerDurableBootIdleTask(deferredTask, { label: 'lazyPhase2' });
  scheduleDurableBootTaskNow();
}

/**
 * Phase 1 apply — optionally splits shell vs phase-1b when dashboard shell gate is enabled.
 */
export function applyPhase1WithOptionalShellGate({
  batchData,
  setters,
  startTransition,
  setDataLoaded,
  setDataShellReady,
  setReady,
  urgent = true,
  replace = false
}) {
  const shellGate = isDashboardShellGateEnabled() && setDataShellReady;

  if (shellGate) {
    applyBatchFieldsToSetters(
      batchData,
      setters,
      [...BOOT_SHELL_FIELDS, ...SHELL_ONLY_FIELDS],
      {
        startTransition,
        setDataShellReady,
        markDataShellReady: true,
        urgent,
        replace
      }
    );
    markBootGate('data_shell_ready', {
      productsCount: batchData.products?.length ?? 0
    });
    measureBoot('business_load_start', 'data_shell_ready', 'data_shell');
    if (setReady) setReady(true);

    applyBatchFieldsToSetters(batchData, setters, BOOT_SHELL_PHASE1B_FIELDS, {
      startTransition,
      setDataLoaded,
      markDataLoaded: true,
      urgent,
      replace
    });
    return;
  }

  const phase1Fields = [...LAZY_BOOT_PRIORITY_FIELDS, ...SHELL_ONLY_FIELDS];
  applyBatchFieldsToSetters(batchData, setters, phase1Fields, {
    startTransition,
    setDataLoaded,
    markDataLoaded: true,
    urgent,
    replace
  });
  if (setDataShellReady) {
    setDataShellReady(true);
  }
  if (setReady) setReady(true);
}

/**
 * Phase 1: POS-critical data → unlock UI immediately from blob when possible.
 * Phase 2: remaining domains in idle/background.
 */
export async function executeLazyPhasedLoad({
  businessId,
  batchData,
  noKvBlobMode,
  shellBlob,
  entityAuthority,
  setters,
  setDataLoaded,
  setDataShellReady,
  setReady,
  startTransition
}) {
  const phase1Started = performance.now();
  const entityContext = { entityAuthority, noKvBlobMode, shellBlob };
  const fastBlobPath =
    hasBatchCorePosData(batchData) && !noKvBlobMode && !shellBlob && !entityAuthority;
  /** @type {{ field: string, error: string }[]} */
  const phase1FailedFields = [];

  if (fastBlobPath) {
    seedPriorityReadCachesSync(businessId, batchData);
  } else {
    try {
      await withDeadline(
        (async () => {
          const readers = await importPriorityEntityReaders();
          const fieldsToRead = getFieldsNeedingEntityRead(
            LAZY_BOOT_PRIORITY_FIELDS,
            batchData,
            entityContext
          );
          if (fieldsToRead.length > 0) {
            const priorityCanonical = await readEntityFieldsParallel(
              businessId,
              fieldsToRead,
              readers,
              { failedOut: phase1FailedFields }
            );
            mergeCanonicalFields(batchData, priorityCanonical, { businessId });
          }
          try {
            await withDeadline(
              resyncEntityStoresIfCoreEmpty(batchData, businessId, noKvBlobMode, readers),
              ENTITY_RESYNC_DEADLINE_MS,
              'entity_resync_if_empty'
            );
          } catch (resyncTimeoutErr) {
            warnLoad('entity resync budget exceeded — continuing Phase 1', resyncTimeoutErr);
          }
        })(),
        PHASE1_ENTITY_READ_BUDGET_MS,
        'phase1_entity_reads'
      );
    } catch (phase1ReadErr) {
      warnLoad('Phase-1 entity read budget exceeded — unlocking with partial data', phase1ReadErr);
      markBootGate('business_phase1_partial', {
        reason: phase1ReadErr?.message || 'budget',
        productsCount: batchData.products?.length ?? 0
      });
    }
  }

  const bizType =
    batchData.businessInfo?.business_type || batchData.metadata?.business_type || '';
  const isRestaurant = String(bizType).toLowerCase().includes('restaurant');
  if (isRestaurant) {
    try {
      await applyRestaurantProductPrep(batchData, businessId);
    } catch (restErr) {
      warnLoad('restaurant prep skipped', restErr);
    }
  }

  // Always unlock shell — even if some fields failed (partial > stuck skeleton).
  applyPhase1WithOptionalShellGate({
    batchData,
    setters,
    startTransition,
    setDataLoaded,
    setDataShellReady,
    setReady,
    urgent: true,
    replace: consumeBusinessSwitchReplace(businessId)
  });
  initializeInstantCacheFromBatch(batchData, businessId);

  markBootGate('business_phase1_done', {
    fastBlobPath,
    productsCount: batchData.products?.length ?? 0,
    shellGate: isDashboardShellGateEnabled()
  });
  setBootMeta('lazyBootFastBlobPath', fastBlobPath);
  measureBoot('business_load_start', 'business_phase1_done', 'business_phase1');

  logAchatsReload('load.lazyPhase1', {
    businessId,
    fastBlobPath,
    purchasesNormalizedCount: batchData.purchases?.length ?? 0,
    productsCount: batchData.products?.length ?? 0,
    failedFields: phase1FailedFields.map((f) => f.field),
    shellBlob,
    shellGate: isDashboardShellGateEnabled(),
    elapsedMs: Math.round(performance.now() - phase1Started)
  });

  scheduleDeferredLoad(async () => {
    const phase2Started = performance.now();
    try {
      const retryPriorityFields = [
        ...new Set([
          ...phase1FailedFields.map((f) => f.field),
          ...(!hasBatchCorePosData(batchData) ? LAZY_BOOT_PRIORITY_FIELDS : [])
        ])
      ].filter((field) => LAZY_BOOT_PRIORITY_FIELDS.includes(field));

      if (fastBlobPath) {
        // Phase-1 already painted from blob. Only re-apply priority fields when thin,
        // otherwise this storms React on the open dashboard.
        const thinPriority =
          !(batchData.products?.length > 0) || countLotEntries(batchData.lots) === 0;
        if (thinPriority) {
          const readers = await importPriorityEntityReaders();
          const refreshed = await readEntityFieldsParallel(
            businessId,
            LAZY_BOOT_PRIORITY_FIELDS,
            readers
          );
          mergeCanonicalFields(batchData, refreshed, { businessId });
          if (!isRestaurant) {
            await applyRestaurantProductPrep(batchData, businessId);
          }
          applyBatchFieldsToSetters(batchData, setters, LAZY_BOOT_PRIORITY_FIELDS, {
            startTransition,
            setDataLoaded,
            markDataLoaded: false
          });
          initializeInstantCacheFromBatch(batchData, businessId, { force: false });
        }
      } else {
        if (retryPriorityFields.length > 0) {
          try {
            const readers = await importPriorityEntityReaders();
            const recovered = await readEntityFieldsParallel(
              businessId,
              retryPriorityFields,
              readers,
              { deadlineMs: Math.round(ENTITY_FIELD_READ_DEADLINE_MS * 1.5) }
            );
            mergeCanonicalFields(batchData, recovered, { businessId });
            applyBatchFieldsToSetters(batchData, setters, retryPriorityFields, {
              startTransition,
              setDataLoaded,
              markDataLoaded: false
            });
            initializeInstantCacheFromBatch(batchData, businessId, { force: false });
            markBootGate('business_phase1_retry_done', {
              fields: retryPriorityFields,
              productsCount: batchData.products?.length ?? 0
            });
          } catch (retryErr) {
            warnLoad('Phase-1 field retry failed', retryErr);
          }
        }
        if (!isRestaurant) {
          try {
            await applyRestaurantProductPrep(batchData, businessId);
            applyBatchFieldsToSetters(batchData, setters, ['products', 'restaurantDishes'], {
              startTransition,
              setDataLoaded,
              markDataLoaded: false
            });
          } catch (restErr) {
            warnLoad('phase2 restaurant prep skipped', restErr);
          }
        }
      }

      try {
        purgeLegacyStandaloneDomainKeys(businessId);
      } catch (legacyErr) {
        warnLoad('legacy standalone domain key cleanup skipped', legacyErr);
      }

      const readers = await importAllEntityReaders();
      try {
        const writerWait = await waitForEntityStoreWritersIdle({
          maxWaitMs: PHASE2_WRITER_WAIT_MS,
          label: 'lazyPhase2'
        });
        if (writerWait.timedOut) {
          warnLoad('Phase-2 starting while entity writers still active', {
            writersActive: writerWait.writersActive,
            waitedMs: writerWait.waitedMs
          });
          markBootGate('business_phase2_writer_wait_timeout', writerWait);
        } else if (writerWait.waited) {
          markBootGate('business_phase2_writer_wait_done', writerWait);
        }
      } catch (waitErr) {
        warnLoad('Phase-2 writer wait skipped', waitErr);
      }

      const deferredCanonical = await readEntityFieldsParallel(
        businessId,
        LAZY_BOOT_DEFERRED_FIELDS,
        readers,
        {
          concurrency: ENTITY_FIELD_READ_CONCURRENCY,
          batchLabel: 'lazyPhase2'
        }
      );
      mergeCanonicalFields(batchData, deferredCanonical, { businessId });

      batchData.expiredProducts = mergeExpiredProductsForDisplay(
        batchData.expiredProducts || [],
        asArray(batchData.lots),
        batchData.products || []
      );

      await postProcessSupermarketInvoicesBatch(batchData, businessId);

      applyBatchFieldsToSetters(batchData, setters, LAZY_BOOT_DEFERRED_FIELDS, {
        startTransition,
        setDataLoaded,
        markDataLoaded: false
      });
      updateInstantCacheDeferredFields(batchData);

      logAchatsReload('load.lazyPhase2', {
        businessId,
        salesCount: batchData.sales?.length ?? 0,
        ordersCount: batchData.orders?.length ?? 0,
        elapsedMs: Math.round(performance.now() - phase2Started)
      });

      scheduleAggregateIndexBackfill(() => runAggregateIndexBackfill(batchData, businessId));

      dispatchBusinessLazyLoadComplete(businessId);
      markBootGate('business_phase2_done', { businessId });
      measureBoot('business_phase1_done', 'business_phase2_done', 'business_phase2');
    } catch (deferredErr) {
      warnLoad('lazy boot phase 2 failed', deferredErr);
      markBootGate('business_phase2_failed', {
        businessId,
        error: deferredErr?.message || String(deferredErr)
      });
      // Still signal completion so POS/gates are not blocked forever.
      try {
        dispatchBusinessLazyLoadComplete(businessId);
      } catch {
        /* ignore */
      }
    }
  });
}
